import { useContext, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { Container, Stack } from 'react-bootstrap';

import { UniqueWidgetRefreshContext } from 'App';
import ForecastSpread from 'components/ForecastSpread';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import { useAPIData } from 'hooks/useAPIData';
import useDataRefresh from 'hooks/useDataRefresh';
import { DateToString } from 'utils/stringUtils';
import { getDetailedMarine7 } from 'services/weather/marine.service';

const ROUTE = 'getDetailedMarine7';
const DAY_SEGMENTS = ['Morning', 'Afternoon', 'Evening', 'Night'];

export default function SurfAlerts() {
  const currentDateStr = DateToString(new Date());

  const [data, setData] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const api = useAPIData();
  const uwf = useContext(UniqueWidgetRefreshContext);
  const refreshTokens = useDataRefresh();

  const locationKey = `${Cookies.get('city')}-${Cookies.get('state')}`;
  const uniqueKey = `${locationKey}-${ROUTE}`;

  const groupedData = useMemo(() => {
    return data.reduce((acc, entry) => {
      const dayKey = new Date(entry.iso8601).setHours(0, 0, 0, 0);
      if (!acc[dayKey]) {
        acc[dayKey] = {};
      }
      acc[dayKey][entry.day_segment] = entry;
      return acc;
    }, {});
  }, [data]);

  const loadData = async (force = false) => {
    const hasExistingData = data.length > 0;
    if (!hasExistingData) {
      setIsInitialLoad(true);
    } else {
      setIsRefreshing(true);
    }

    try {
      const requester = force ? api.forceGet : api.get;
      const response = await requester({
        key: ROUTE,
        apiFunction: getDetailedMarine7,
      });
      setData(response?.data?.result ?? []);
    } catch (error) {
      console.error('Error fetching surf alerts:', error);
    } finally {
      setIsInitialLoad(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!uwf[uniqueKey]) return;
    loadData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uwf, uniqueKey]);

  useEffect(() => {
    loadData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTokens.widgetRefresh]);

  if (isInitialLoad) {
    return <WidgetLoadingAnimation />;
  }

  return (
    <Container
      fluid
      className="d-flex flex-column h-100 px-0 user-select-none text-white overflow-auto"
    >
      <WidgetTimeUpdate route={ROUTE} dataUpdateOnly addMargin />

      <div className="border-bottom border-secondary-subtle pb-2 mb-4 fs-5">
        {`${currentDateStr.month} ${currentDateStr.day}, ${currentDateStr.year} Current Surf Alerts`}
      </div>

      <Stack gap={5} className="pb-4">
        {Object.entries(groupedData).map(([dayKey, segments]) => {
          const firstAvailableSegment = DAY_SEGMENTS.find((segment) => segments[segment]);
          const segmentEntry = segments[firstAvailableSegment];
          const dayDate = new Date(Number(dayKey));

          return (
            <div key={dayKey}>
              <div className="text-center fw-bold fs-4 mb-3">
                {`${segmentEntry?.weekday ?? ''} ${dayDate.toLocaleDateString()}`}
                {isRefreshing && <span className="ms-2 small text-secondary">Updating…</span>}
              </div>

              <Stack gap={3}>
                {DAY_SEGMENTS.filter((segment) => segments[segment]).map((segment) => {
                  const entry = segments[segment];
                  return (
                    <ForecastSpread
                      key={`${dayKey}-${segment}`}
                      header={segment}
                      headerStyle={{ fontWeight: 'normal', fontSize: 15 }}
                      data={{
                        Wave: entry.wave,
                        Alert: entry.surf,
                        Wind: entry.wind,
                      }}
                    />
                  );
                })}
              </Stack>
            </div>
          );
        })}
      </Stack>
    </Container>
  );
}
